import { Color, Flex, Icon, Text } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import type { TransactionStatus } from '@features/bankTransactions/types/transactionStatus'
import { getBankLineDescription } from '@features/bankTransactions/utils/getBankLineDescription'
import { BankLine } from '@modules-deprecated/bankReconciliation/types'

import { useBankTransactionDrawer } from '../../../../contexts'
import { transactionTypeToIcon } from '../../constants/transactionTypeToIcon'
import { transactionTypeToTranslationKey } from '../../constants/transactionTypeToTranslationKey'
import { formatTransactionAmount } from '../../utils/formatTransactionAmount'
import { formatTransactionType } from '../../utils/formatTransactionType'
import { StatusIndicator } from './elements/StatusIndicator'
import * as Styled from './styles'

interface BankTransactionProps {
  transaction: BankLine
  currency?: string
  isPending?: boolean
  hasSuggestedMatch?: boolean
}

export const BankTransaction = ({
  transaction,
  currency,
  isPending,
  hasSuggestedMatch,
}: BankTransactionProps): ReactElement => {
  const { description, amount, type, side, match } = transaction

  const { t } = useTranslation()
  const { selectTransaction } = useBankTransactionDrawer()

  const formattedAmount = formatTransactionAmount(amount, side, currency)
  const formattedType = formatTransactionType(type, side)
  const icon = transactionTypeToIcon[formattedType]
  const typeLabel = t(transactionTypeToTranslationKey[formattedType])
  const getMatchStatus = (): TransactionStatus => {
    if (match?.isApproved) {
      return 'markedAsReady'
    }
    if (hasSuggestedMatch) {
      return 'reviewSuggestion'
    }
    return 'needsReview'
  }
  const matchStatus = getMatchStatus()

  const handleClick = useCallback(() => {
    if (isPending) {
      return
    }

    selectTransaction(transaction)
  }, [isPending, selectTransaction, transaction])

  return (
    <Styled.RowWrapper onClick={isPending ? undefined : handleClick} isPending={isPending}>
      <Styled.IconWrapper>
        <Icon icon={icon} color={Color.Grey950} />
      </Styled.IconWrapper>
      <Text weight="medium">{getBankLineDescription(description)}</Text>
      <Styled.RightAlignedColumn>
        <Text colorVariant="discreet">{typeLabel}</Text>
      </Styled.RightAlignedColumn>
      <Styled.RightAlignedColumn>
        <Text variant="bodyLG" weight="medium" colorVariant={side === 'debit' ? 'success' : 'primary'}>
          {formattedAmount}
        </Text>
      </Styled.RightAlignedColumn>
      <Flex justifyContent="flex-end">
        <StatusIndicator status={matchStatus} />
      </Flex>
    </Styled.RowWrapper>
  )
}
